import { useEffect, useRef } from "react";
import uPlot from "uplot";
import "uplot/dist/uPlot.min.css";
import { wheelZoomPlugin, applySyncRange } from "@/lib/uplot-zoom";

// COMTRADE waveform viewer — imperative uPlot, not React-state-driven.
// Follows the research appendix §A pattern: single instance, keep data outside
// React, destroy on unmount.
//
// Dual y-axes: voltages on the left (V scale), currents on the right (A scale).
// Pre-trigger region shaded via a drawClear plugin (rectangle painted before
// series draw so series lines appear on top).

export interface RecommendedTrim {
  start_ms: number;
  end_ms: number;
}

export interface WaveformData {
  time_ms: number[];
  channels: Record<string, { unit?: string | null; values: (number | null)[] }>;
  trigger_sample?: number | null;
  sample_rate_hz?: number | null;
  decimated?: boolean;
  decimated_from?: number | null;
  // Advisory trim recommendation (U7) — present on the COMTRADE waveform
  // response when the codec bracketed a disturbance. Read-only metadata.
  recommended_trim?: RecommendedTrim | null;
}

const V_COLORS = ["#ef4444", "#f59e0b", "#eab308"]; // Va/Vb/Vc — red/amber/yellow
const I_COLORS = ["#3b82f6", "#10b981", "#a855f7"]; // Ia/Ib/Ic — blue/emerald/violet

function classifyChannel(label: string): "V" | "A" | "other" {
  const up = label.toUpperCase();
  if (up.startsWith("V")) return "V";
  if (up.startsWith("I") || up.startsWith("A")) return "A";
  return "other";
}

function preTriggerPlugin(getTriggerMs: () => number | null): uPlot.Plugin {
  return {
    hooks: {
      drawClear: (u) => {
        const triggerMs = getTriggerMs();
        if (triggerMs == null) return;
        const xMin = u.scales.x?.min;
        if (xMin == null || triggerMs <= xMin) return;
        const ctx = u.ctx;
        const left = u.bbox.left;
        const top = u.bbox.top;
        const height = u.bbox.height;
        const xTrig = u.valToPos(triggerMs, "x", true);
        const width = xTrig - left;
        if (width <= 0) return;
        ctx.save();
        // Pre-trigger region — subtle overlay so lines on top stay readable.
        ctx.fillStyle = "rgba(255, 180, 0, 0.08)";
        ctx.fillRect(left, top, width, height);
        // Vertical trigger marker line
        ctx.strokeStyle = "rgba(255, 120, 0, 0.6)";
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.moveTo(xTrig, top);
        ctx.lineTo(xTrig, top + height);
        ctx.stroke();
        ctx.restore();
      },
    },
  };
}

// Horizontal reference line at the connected service's breaker capacity.
// Drawn on the "A" scale (the current y-axis) so operators can eyeball
// "did this swell push past my service capacity?" without doing the math.
function serviceCapacityPlugin(
  getCapacityAmps: () => number | null,
): uPlot.Plugin {
  return {
    hooks: {
      draw: (u) => {
        const cap = getCapacityAmps();
        if (cap == null || cap <= 0) return;
        // Resolve to pixel y on the "A" scale. valToPos returns NaN when
        // the scale isn't initialised yet — guard explicitly.
        const yPx = u.valToPos(cap, "A", true);
        if (!Number.isFinite(yPx)) return;
        const top = u.bbox.top;
        const height = u.bbox.height;
        if (yPx < top || yPx > top + height) return;
        const ctx = u.ctx;
        ctx.save();
        ctx.strokeStyle = "rgba(244, 63, 94, 0.85)";  // rose-500
        ctx.lineWidth = 1.5;
        ctx.setLineDash([6, 4]);
        ctx.beginPath();
        ctx.moveTo(u.bbox.left, yPx);
        ctx.lineTo(u.bbox.left + u.bbox.width, yPx);
        ctx.stroke();
        ctx.setLineDash([]);
        // Inline label so the user knows what the line is.
        ctx.font = "11px ui-sans-serif, system-ui, sans-serif";
        ctx.fillStyle = "rgba(244, 63, 94, 0.95)";
        ctx.textBaseline = "bottom";
        const label = `Service cap ${cap.toFixed(0)}A`;
        const textW = ctx.measureText(label).width;
        const labelX = u.bbox.left + u.bbox.width - textW - 8;
        ctx.fillText(label, labelX, yPx - 3);
        ctx.restore();
      },
    },
  };
}

// Advisory trim region (U7) — a read-only shaded band between start_ms and
// end_ms. Same drawClear technique as preTriggerPlugin; nothing here is
// interactive (no draggable handles — interactive editing is deferred work).
function recommendedTrimPlugin(
  getTrim: () => RecommendedTrim | null
): uPlot.Plugin {
  return {
    hooks: {
      drawClear: (u) => {
        const trim = getTrim();
        if (trim == null) return;
        const xMin = u.scales.x?.min;
        const xMax = u.scales.x?.max;
        if (xMin == null || xMax == null) return;
        const s = Math.max(trim.start_ms, xMin);
        const e = Math.min(trim.end_ms, xMax);
        if (e <= s) return;
        const xs = u.valToPos(s, "x", true);
        const xe = u.valToPos(e, "x", true);
        const top = u.bbox.top;
        const height = u.bbox.height;
        const ctx = u.ctx;
        ctx.save();
        ctx.fillStyle = "rgba(56, 189, 248, 0.10)"; // sky — distinct from amber pre-trigger
        ctx.fillRect(xs, top, xe - xs, height);
        ctx.strokeStyle = "rgba(56, 189, 248, 0.55)";
        ctx.lineWidth = 1;
        ctx.setLineDash([5, 3]);
        ctx.beginPath();
        ctx.moveTo(xs, top);
        ctx.lineTo(xs, top + height);
        ctx.moveTo(xe, top);
        ctx.lineTo(xe, top + height);
        ctx.stroke();
        ctx.restore();
      },
    },
  };
}

export function WaveformChart({
  data,
  height = 420,
  syncRange,
  onSyncRangeChange,
  showHint = true,
  recommendedTrim = null,
  serviceCapacityAmps = null,
}: {
  data: WaveformData;
  height?: number;
  // Externally-controlled x-range. When provided, the chart applies it
  // imperatively (no rebuild) and reports its own zoom changes back via
  // onSyncRangeChange — let two charts share zoom by giving each the same
  // useState pair.
  syncRange?: [number, number] | null;
  onSyncRangeChange?: (range: [number, number]) => void;
  showHint?: boolean;
  // Advisory trim window (ms). When set, a read-only shaded band is drawn.
  // Passed as a prop (not read from `data`) so the COMTRADE-derived
  // recommendation can also be shown on the fastlog chart.
  recommendedTrim?: RecommendedTrim | null;
  // Service breaker capacity (A) — when set, drawn as a dashed horizontal
  // reference line on the right ("A") axis.
  serviceCapacityAmps?: number | null;
}) {
  const hostRef = useRef<HTMLDivElement>(null);
  const uRef = useRef<uPlot | null>(null);
  const triggerMsRef = useRef<number | null>(null);
  // Stamped explicitly inside the init effect before each uPlot build — see
  // there — so the trim plugin's first drawClear reads the right value
  // without depending on effect-ordering.
  const trimRef = useRef<RecommendedTrim | null>(null);
  // Stable ref for the service cap so the plugin (built once at uPlot init)
  // reads the latest prop value on every redraw without rebuilding the chart.
  const capRef = useRef<number | null>(serviceCapacityAmps);
  capRef.current = serviceCapacityAmps;
  // Stable ref to onSyncRangeChange so the wheel plugin (built once at
  // uPlot init) always calls the latest handler without rebuilding the chart.
  const onSyncRef = useRef(onSyncRangeChange);
  onSyncRef.current = onSyncRangeChange;

  useEffect(() => {
    if (!hostRef.current) return;

    // Stamp the trim before building uPlot so the plugin's first draw is
    // correct on a chart rebuild (the [recommendedTrim] effect below only
    // covers later prop changes).
    trimRef.current = recommendedTrim;

    const labels = Object.keys(data.channels);
    const vLabels = labels.filter((l) => classifyChannel(l) === "V").slice(0, 3);
    const iLabels = labels.filter((l) => classifyChannel(l) === "A").slice(0, 3);
    const orderedLabels = [...vLabels, ...iLabels];

    // AlignedData: [x-series, ...y-series]
    // Defensive: derived/back-filled fastlogs may arrive without time_ms
    // populated (pre-2026-06-02 backfill). Build a synthetic axis from
    // sample_rate_hz so the units match the sibling COMTRADE chart's
    // x-axis (ms) — otherwise the shared syncRange propagates a value
    // that means different things in each chart on zoom and the two go
    // visually out of sync. Last-resort fallback is sample-index (still
    // beats crashing).
    const maxSamples = Math.max(
      ...Object.values(data.channels).map(c => c.values?.length ?? 0),
    );
    const timeAxis = (data.time_ms && data.time_ms.length > 0)
      ? data.time_ms
      : (data.sample_rate_hz && data.sample_rate_hz > 0)
        ? Array.from({ length: maxSamples }, (_, i) => (i * 1000) / data.sample_rate_hz!)
        : Array.from({ length: maxSamples }, (_, i) => i);
    const alignedData: uPlot.AlignedData = [
      timeAxis,
      ...orderedLabels.map(
        (l) => (data.channels[l].values as (number | null)[]) ?? []
      ),
    ] as unknown as uPlot.AlignedData;

    // Compute trigger in ms (per-sample) if available
    triggerMsRef.current =
      data.trigger_sample != null && timeAxis[data.trigger_sample] != null
        ? timeAxis[data.trigger_sample]
        : null;

    const series: uPlot.Series[] = [
      { label: "t" },
      ...vLabels.map(
        (label, i) => ({
          label,
          scale: "V",
          stroke: V_COLORS[i % V_COLORS.length],
          width: 1,
          // Voltage series start hidden — for a current swell the current
          // traces are what matter, and three V lines on top crowd the
          // chart. Click the legend label to toggle a phase on.
          show: false,
        } as uPlot.Series)
      ),
      ...iLabels.map(
        (label, i) => ({
          label,
          scale: "A",
          stroke: I_COLORS[i % I_COLORS.length],
          width: 1,
          dash: [4, 2],
        } as uPlot.Series)
      ),
    ];

    const opts: uPlot.Options = {
      width: hostRef.current.clientWidth,
      height,
      scales: {
        x: { time: false },
        V: { auto: true },
        A: { auto: true },
      },
      series,
      axes: [
        {
          label: "ms",
          grid: { stroke: "rgba(255,255,255,0.06)" },
          stroke: "rgba(255,255,255,0.6)",
        },
        {
          scale: "V",
          label: "V",
          side: 3,
          grid: { stroke: "rgba(255,255,255,0.06)" },
          stroke: "rgba(255,255,255,0.6)",
          values: (_u, vs) => vs.map((v) => `${v.toFixed(0)}`),
        },
        {
          scale: "A",
          label: "A",
          side: 1,
          grid: { show: false },
          stroke: "rgba(255,255,255,0.6)",
          values: (_u, vs) => vs.map((v) => `${v.toFixed(1)}`),
        },
      ],
      plugins: [
        preTriggerPlugin(() => triggerMsRef.current),
        recommendedTrimPlugin(() => trimRef.current),
        serviceCapacityPlugin(() => capRef.current),
        wheelZoomPlugin({
          factor: 0.85,
          onChange: (range) => onSyncRef.current?.(range),
        }),
      ],
      // uPlot's own drag is fully off — wheelZoomPlugin does click-drag zoom
      // with its own mouse listeners so the result routes through the shared
      // setScale + onChange (sync) path. select.show:true lets the plugin
      // paint the rubber-band box via u.setSelect.
      cursor: {
        drag: { x: false, y: false, setScale: false },
      },
      select: { show: true, left: 0, top: 0, width: 0, height: 0 },
      legend: { show: true },
    };

    uRef.current = new uPlot(opts, alignedData, hostRef.current);

    const ro = new ResizeObserver((entries) => {
      const w = entries[0].contentRect.width;
      uRef.current?.setSize({ width: w, height });
    });
    ro.observe(hostRef.current);

    return () => {
      ro.disconnect();
      uRef.current?.destroy();
      uRef.current = null;
    };
  }, [data, height]);

  // Apply external syncRange imperatively without rebuilding uPlot.
  useEffect(() => applySyncRange(uRef.current, syncRange ?? null), [syncRange]);

  // Update the advisory trim band without rebuilding uPlot — the waveform
  // response arrives after the chart first mounts (fastlog chart especially).
  // Skip the redraw when the value is unchanged (e.g. the initial mount, where
  // the init effect already stamped trimRef) to avoid a redundant full repaint.
  useEffect(() => {
    if (trimRef.current === recommendedTrim) return;
    trimRef.current = recommendedTrim;
    uRef.current?.redraw();
  }, [recommendedTrim]);

  return (
    <div>
      <div ref={hostRef} style={{ width: "100%", height }} />
      {recommendedTrim && (
        <div className="mt-1 text-[10px] text-sky-400/80">
          Shaded band = recommended trim window (advisory — full capture
          retained).
        </div>
      )}
      {showHint && (
        <div className="mt-1 text-[10px] text-muted-foreground">
          Drag or scroll = zoom · Shift+scroll = pan · double-click = reset ·
          voltages hidden — click a V in the legend to show
        </div>
      )}
      {data.decimated && (
        <div className="mt-2 text-xs text-muted-foreground">
          Decimated from {data.decimated_from?.toLocaleString()} to{" "}
          {(data.time_ms?.length ?? 0).toLocaleString()} points (min/max preserved).
        </div>
      )}
    </div>
  );
}
